import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Dialog from 'src/components/common/Dialog';
import useFetch from 'use-http';
import Personnel from 'src/models/personnel/Personnel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
// import User from 'src/models/User';
import DefaultPay from 'src/models/personnel/DefaultPay';
// import DefaultBill from 'src/models/personnel/DefaultBill';
import moment from 'moment';
import PersonnelToast from '../components/PersonnelToast';
import ExistingLmMatchDialog from './ExistingLmMatchDialog';
import actions from 'src/actions';
import DialogList from 'src/dialogs';

import './PersonnelDialogStyle.scss';
import Landman from 'src/models/personnel/Landman';
import CondensedPersonnel from 'src/models/personnel/CondensedPersonnel';
import _ from 'lodash';
import TSDILandman from 'src/models/personnel/TSDILandman';

interface IAddPersonnelDialogProps {
    dialogName: string;
    hideDialog: () => void;
    setSelectedPersonnel: (personnel: TSDILandman) => void;
}

type DropDownSelection = {
    code: string,
    dscr: string
};

const AddPersonnelDialog = (props: IAddPersonnelDialogProps) => {
    const dispatch = useDispatch();
    const { handleSubmit, register, errors, watch, getValues } = useForm();
    const { get, post, response } = useFetch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [irsCategoryTypes, setIrsCategoryTypes] = useState<Array<DropDownSelection>>(undefined);
    const [personnelTypes, setPersonnelTypes] = useState<Array<DropDownSelection>>(undefined);
    const [billingOffices, setBillingOffices] = useState<Array<DropDownSelection>>(undefined);
    const [personnelList, setPersonnelList] = useState<Array<CondensedPersonnel>>(undefined);
    // Populated when the just-entered name already matches one or more existing LM rows (see
    // checkExistingLmMatches) - lets EXISTING_LM_MATCH offer those as reuse candidates instead of
    // silently letting "Add Personnel" create a second, disconnected LM_NO/TSD_NO for the same person.
    const [existingMatches, setExistingMatches] = useState<Array<CondensedPersonnel>>([]);
    const [pendingLandmanData, setPendingLandmanData] = useState<Personnel | undefined>(undefined);

    const appContext = useContext<IAppContextState>(AppContext);

    const watchStatus = watch('status'); // watch variable for employee type to control required fields (ex: ssn vs tax id)

    const [termsAndCategories, setTermsAndCategories] = useState({ // dict for multi category search
        firstName: '',
        lastName: '',
        corporationName: '',
        username: '',
        ssn: '',
        federalTaxId: ''
    });

    // Initial render
    useEffect(() => {
        onCreate();

        return () => {
            onDestroy();
        };
    }, []);

    const onCreate = () => {
        getIrsCategories();
        getPersonnelTypes();
        getBillingOffices();
        console.log('AddPersonnelDialog onCreate()');
    };

    const onDestroy = () => {
        console.log('AddPersonnelDialog onDestroy()');
    };

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    // get and map irs categories
    const getIrsCategories = async () => {
        let result = await get(`/v1.0/landmen/irs-categories`);
        if (response.ok && result) {
            setIrsCategoryTypes(result.map((ct: DropDownSelection) => {
                return { code: ct.code, dscr: ct.dscr };
            }));
        } else {
            console.log('error with get: ', response);
        }
    };

    const irsCategoryTypeDropdown = () => {
        return irsCategoryTypes
            ? irsCategoryTypes?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    // get and map personnel types
    const getPersonnelTypes = async () => {
        let filteredTypes = ['B', 'P', 'D'];
        let result = await get(`/v1.0/landmen/landman-types`);

        if (response.ok && result) {
            setPersonnelTypes(result.map((ct: DropDownSelection) => {
                return { code: ct.code, dscr: ct.dscr };
            }).filter((c: DropDownSelection) => !filteredTypes.includes(c.code)));
        } else {
            console.log('error with get: ', response);
        }
    };

    const personnelTypeDropdown = () => {
        return personnelTypes
            ? personnelTypes?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    // get and map billing offices
    const getBillingOffices = async () => {
        let result = await get(`/v1.0/landmen/billing-offices`);
        if (response.ok && result) {
            setBillingOffices(result.map((ct: DropDownSelection) => {
                return { code: ct.code, dscr: ct.dscr };
            }));
        } else {
            console.log('error with get: ', response);
        }
    };

    const billingOfficeDropdown = () => {
        return billingOffices
            ? billingOffices?.map((ct: DropDownSelection) => {
                return { value: ct.code, label: ct.dscr };
            })
            : [];
    };

    // on submit
    const addPersonnel = async (data: Personnel) => {
        // validation
        let values = getValues();

        const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i; // Regex pattern for email
        const ssnPattern = /^(?!666|000|9\d{2})\d{3}-(?!00)\d{2}-(?!0000)\d{4}$/; // Regex pattern SSN
        const einPattern = /^\d{2}-\d{7}$/; // Regex pattern EIN
        const altSsnPattern = /^(?!666|000|9\d{2})\d{3}(?!00)\d{2}(?!0000)\d{4}$/; // Regex pattern SSN
        const altEinPattern = /^\d{2}\d{7}$/; // Regex pattern EIN

        let validated = true;
        let errorMessage = '';

        showToast('Submitting data ', `rgba(78,117,154,0.5)`, true);

        if (data.firstName.length === 0 || data.lastName.length === 0 || data.username.length === 0 ||
            (watchStatus === 'E' ? data.ssn.length === 0 : data.federalTaxId.length === 0)) {
            validated = false;
            errorMessage += 'Required info missing\n';
        }

        // NOTE - after talking to Annika, the system should still allow users to be created even if the following:

        // username exists
        // ssn exists
        // eid exists
        // email exists
        // first + last name

        // if (props.personnelList.find(p => p.firstName === data.firstName && p.lastName === data.lastName)) {
        //     validated = false;
        //     errorMessage += `User (${data.firstName} ${data.lastName}) already exists\n`;
        // }

        // if (users.find(u => u.username === data.username)) {
        //     validated = false;
        //     errorMessage += `A user with that email already exists\n`;
        // }

        // if (props.personnelList.find(p => p.ssn === data.ssn)) {
        //     validated = false;
        //     errorMessage += `A user with that SSN already exists\n`;
        // }

        if (!emailPattern.test(data.username)) {
            validated = false;
            errorMessage += 'Invalid email (e.g. example@mail.com)\n';
        }

        if (watchStatus === 'E') {
            if (!ssnPattern.test(data.ssn) && !altSsnPattern.test(data.ssn)) {
                validated = false;
                errorMessage += 'Invalid SSN, must be 9 digits (e.g. XXX-XX-XXXX or XXXXXXXXX)\n';
            }

            if (data.ssn !== values.verifySSN) {
                validated = false;
                errorMessage += 'SSNs do not match\n';
            }
        }

        if (watchStatus === 'C' || watchStatus === 'S') {
            if (!einPattern.test(data.federalTaxId) && !altEinPattern.test(data.federalTaxId)) {
                validated = false;
                errorMessage += 'Invalid EIN, must be 9 digits (e.g. XX-XXXXXX or XXXXXXXXX)\n';
            }

            if (data.federalTaxId !== values.verifyTaxId) {
                validated = false;
                errorMessage += 'Tax Ids do not match';
            }
        }

        if (validated) {
            // A same-name LM row already existing doesn't itself invalidate the form - it just
            // means EXISTING_LM_MATCH should offer reusing it before falling through to create-landman.
            const matches = await checkExistingLmMatches(data.firstName, data.lastName);
            if (matches.length > 0) {
                setExistingMatches(matches);
                setPendingLandmanData(data);
                dispatch(actions.layout.showDialog(DialogList.EXISTING_LM_MATCH));
                return;
            }

            await createNewLmAndPersonnel(data);
        } else {
            showToast(errorMessage, `rgba(255,0,0,0.5)`);
        }
    };

    // Exact-name check against LM, independent of whatever partial term(s) the live as-you-type
    // search (handleSearch/personnelList) happens to be showing - this always checks first+last
    // name specifically, matching what "Add Personnel" is actually about to create.
    const checkExistingLmMatches = async (firstName: string, lastName: string): Promise<Array<CondensedPersonnel>> => {
        const params = new URLSearchParams({ firstName, lastName }).toString();
        const results = await get(`v1.0/landmen/personnel/get-by-multi-category?${params}`);
        return response.ok && Array.isArray(results) ? results : [];
    };

    // Second half of Add Personnel, shared by both the no-match path (a fresh TSD_NO from
    // create-landman) and the EXISTING_LM_MATCH "Use This" path (an already-existing TSD_NO) -
    // creates the LMS User/PersonDetails row and bridges it to whichever tsdNo it's given.
    const finishCreatingPersonnelForTsdNo = async (data: Personnel, tsdNo: number) => {
        data.tsdNo = tsdNo;
        let newLandman = await post(`/v1.0/landmen/`, data);

        if (response.ok) {
            createDefaultPayUnit(finalizeDefaultPayUnit(newLandman.id));
            // createDefaultBillUnit(finalizeDefaultBillUnit(newId));
            showToast('Create personnel successful');

            let result = await get(`/v1.0/landmen/personnel/${tsdNo}/get-by-tsd-no`);
            if (response.ok && result) {
                props.setSelectedPersonnel(result);
                props.hideDialog();
            } else {
                showToast('Unable to fetch new personnel: ' + response, `rgba(255,0,0,0.5)`);
                props.hideDialog();
            }
        } else if (newLandman?.messageCode === '15') {
            console.log('create personnel failed: ', newLandman.message);
            showToast('Create personnel failed: ' + newLandman.message, `rgba(255,0,0,0.5)`);
        } else {
            console.log('create personnel failed: ', newLandman.message);
            showToast('Create personnel failed: ' + newLandman.message, `rgba(255,0,0,0.5)`);
        }
    };

    // Original full flow - no existing LM row matched this name, so a brand new one is created
    // via create-landman (usp_AddLandman) before the LMS side is created against its new tsdNo.
    const createNewLmAndPersonnel = async (data: Personnel) => {
        let landman = finalizeLandmanData(data);
        let tsdNo = await post(`/v1.0/landmen/create-landman`, landman);

        if (response.ok) {
            await finishCreatingPersonnelForTsdNo(data, tsdNo);
        } else {
            showToast('Create landman failed: ' + response, `rgba(255,0,0,0.5)`);
        }
    };

    // EXISTING_LM_MATCH's own three actions - all return focus to this dialog first (so its own
    // toast host is visible again for whatever happens next), matching the "sibling dialogs, no
    // same-tick hide+show" pattern used elsewhere rather than trying to render/toast from the other
    // dialog directly.
    const useExistingLmMatch = (tsdNo: number) => {
        dispatch(actions.layout.showDialog(props.dialogName));
        if (pendingLandmanData) {
            finishCreatingPersonnelForTsdNo(pendingLandmanData, tsdNo);
        }
    };

    const createNewDespiteMatch = () => {
        dispatch(actions.layout.showDialog(props.dialogName));
        if (pendingLandmanData) {
            createNewLmAndPersonnel(pendingLandmanData);
        }
    };

    const cancelExistingLmMatch = () => {
        dispatch(actions.layout.showDialog(props.dialogName));
    };

    const finalizeLandmanData = (data: Personnel) => {
        let values = getValues();
        let landman = new Landman();

        landman.billingOffice = data.billingOffice ?? null;
        landman.employeeType = data.status ?? null;
        landman.billRate = 0.00; // per hr
        landman.payRate = parseInt(values?.defaultPayRate, 10) ?? null;
        landman.email = values.username ?? null;
        landman.taxType = data.irsCategoryType ?? null;
        landman.lastName = data.lastName ?? null;
        landman.firstName = data.firstName ?? null;
        landman.ssn = data.ssn ? data.ssn.replace(/[^0-9.]/g, '') : null; // parse out -
        landman.taxId = data.federalTaxId ? data.federalTaxId.replace(/[^0-9.]/g, '') : null; // parse out -
        landman.middleName = data.middleName !== '' ? data.middleName : null;
        landman.corpName = data.corporationName ?? null;
        landman.payUnit = values?.defaultPayUnit ?? null;
        landman.billUnit = values?.defaultPayUnit ?? null;
        landman.managerName = appContext.currentUser.username ?? null;
        landman.managerTsdNo = parseInt(appContext.currentUser.lmsNumber.toString(), 10) ?? null;

        return landman;
    };

    // still adding to LMS, which we can probably get rid of
    const finalizeDefaultPayUnit = (id: number) => {
        let values = getValues();

        let finalData: DefaultPay = {
            id: 0,
            userId: id,
            defaultPayRate: parseInt(values?.defaultPayRate, 10),
            defaultPayUnit: values?.defaultPayUnit,
            isActive: true,
            createdDate: moment(new Date).format('YYYY-MM-DD') as unknown as Date,
            createdBy: appContext.currentUser.username
        };

        return finalData;
    };

    // still adding to LMS, which we can probably get rid of
    const createDefaultPayUnit = async (data: DefaultPay) => {
        if (data?.defaultPayUnit) {
            let result = await post(`/v1.0/landmen/${data?.userId}/create-personnel-defaultPay`, data);

            if (response.ok) {
                // getDefaultPayHistoryByUserId();
                showToast('Personnel updated successfully');
            } else if (result?.messageCode === '15') {
                showToast('Update personnel failed: ' + result, `rgba(255,0,0,0.5)`);
            } else {
                showToast('Update personnel failed: ' + result, `rgba(255,0,0,0.5)`);
            }
        } else {
            showToast('Unable to set default pay for user - no default pay unit provided', `rgba(255,0,0,0.5)`);
        }
    };

    // gets role name from code in duplicate search list
    const getRoleName = (code: string) => {
        return personnelTypes?.find((ct: DropDownSelection) => ct.code === code)?.dscr;
    };

    const handleSearch = _.debounce(async (category: string) => {
        let values = getValues();

        // updating dictionary of categories
        const newTermsAndCategories = {
            ...termsAndCategories,
            [category]: values[category]
        };

        setTermsAndCategories(newTermsAndCategories);

        const params = new URLSearchParams(newTermsAndCategories).toString();
        let results = await get(`v1.0/landmen/personnel/get-by-multi-category?${params}`);

        if (response.ok && results) {
            setPersonnelList(results);
        } else {
            showToast('No results found', `rgba(255,0,0,0.5)`);
            setPersonnelList(null);
        }

    }, 800);

    const onSubmit = async (data: Personnel) => {
        console.log('Add Personnel Dialog OnSubmit :D');
        addPersonnel(data);
    };

    const closeDialog = () => {
        setPersonnelList(null);
        setTermsAndCategories({
            firstName: '',
            lastName: '',
            corporationName: '',
            username: '',
            ssn: '',
            federalTaxId: ''
        });
        props.hideDialog();
    };

    return (
        <>
        <Dialog title="Add Personnel" dialogName={props.dialogName} bodyStyle={{ width: '75%' }} isExitButtonInvisible={true}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="dialog-two-col">
                    <div className="dialog-col-ap" style={{ width: '35%' }}>
                        <RHFSelect
                            bodyStyle={{ marginBottom: '1rem' }}
                            name="status"
                            label="Status"
                            options={personnelTypeDropdown()}
                            register={register}
                            errors={errors}
                            isRequired={true}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                        />
                        <RHFInputBox
                            style={{ width: '50%' }}
                            name="firstName"
                            label="First Name"
                            isRequired={true}
                            register={register}
                            errors={errors}
                            onChange={() => handleSearch('firstName')}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                        />
                        <RHFInputBox
                            style={{ width: '50%' }}
                            name="lastName"
                            label="Last Name"
                            isRequired={true}
                            register={register}
                            errors={errors}
                            onChange={() => handleSearch('lastName')}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                        />
                        <RHFInputBox
                            style={{ width: '50%' }}
                            name="middleName"
                            label="Middle Name"
                            register={register}
                            errors={errors}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                        />
                        <RHFInputBox
                            style={{ width: '50%' }}
                            name="corporationName"
                            label="Corporation"
                            className="lh-half-width"
                            register={register}
                            onChange={() => handleSearch('corporationName')}
                            errors={errors}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                            isRequired={getValues().status === 'S' ? true : false}
                        />
                        <RHFSelect
                            style={{ width: '50%' }}
                            name="billingOffice"
                            label="Office"
                            options={billingOfficeDropdown()}
                            register={register}
                            errors={errors}
                            labelOnLeft={true}
                            isRequired={true}
                            spaceLabelContent={true}
                        />
                        <RHFInputBox
                            style={{ width: '50%' }}
                            name="username"
                            label="Email"
                            className="lh-half-width"
                            register={register}
                            onChange={() => handleSearch('username')}
                            isRequired={true}
                            errors={errors}
                            labelOnLeft={true}
                            spaceLabelContent={true}
                        />
                    </div>

                    {/* second col */}

                    <div className="dialog-col-ap" style={{ width: '65%' }}>
                        <div className="form-row">
                            <div style={{ maxHeight: '20vh', overflowY: 'auto' }}>
                                {personnelList && personnelList.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', maxHeight: '35%', tableLayout: 'fixed' }} id="userSearchTable">
                                        <thead>
                                            <tr style={{ width: '100%' }}>
                                                <th className="table-header" style={{ width: '10%' }}>Number</th>
                                                <th className="table-header" style={{ width: '15%' }}>Role</th>
                                                <th className="table-header" style={{ width: '20%' }}>Name</th>
                                                <th className="table-header" style={{ width: '25%' }}>Corp. Name</th>
                                                <th className="table-header" style={{ width: '15%' }}>Soc. Sec. No.</th>
                                                <th className="table-header" style={{ width: '15%' }}>Tax Id</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {personnelList.map((p: CondensedPersonnel, index) => (
                                                <tr key={`p-${index}`} style={{ width: '100%' }}>
                                                    <td style={{ width: '10%' }}>
                                                        <input type="text" disabled={true} style={{ width: '100%', pointerEvents: 'none' }} value={p.tsdNo} />
                                                    </td>
                                                    <td style={{ width: '15%' }}>
                                                        <input type="text" disabled={true} style={{ width: '100%', pointerEvents: 'none' }} value={getRoleName(p.role)} />
                                                    </td>
                                                    <td style={{ width: '20%' }}>
                                                        <input type="text" disabled={true} style={{ width: '100%', pointerEvents: 'none' }} value={`${p.lastName}, ${p.firstName}`} />
                                                    </td>
                                                    <td style={{ width: '25%' }}>
                                                        <input type="text" disabled={true} style={{ width: '100%', pointerEvents: 'none' }} value={p.corporationName} />
                                                    </td>
                                                    <td style={{ width: '15%' }}>
                                                        <input type="text" disabled={true} style={{ width: '100%', pointerEvents: 'none' }} value={p.ssn} />
                                                    </td>
                                                    <td style={{ width: '15%' }}>
                                                        <input type="text" disabled={true} style={{ width: '100%', pointerEvents: 'none' }} value={p.federalTaxId} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', maxHeight: '35%', tableLayout: 'fixed' }} id="userSearchTable">
                                            <thead>
                                                <tr style={{ width: '100%' }}>
                                                    <th className="table-header" style={{ width: '10%' }}>Number</th>
                                                    <th className="table-header" style={{ width: '15%' }}>Role</th>
                                                    <th className="table-header" style={{ width: '20%' }}>Name</th>
                                                    <th className="table-header" style={{ width: '25%' }}>Corp. Name</th>
                                                    <th className="table-header" style={{ width: '15%' }}>Soc. Sec. No.</th>
                                                    <th className="table-header" style={{ width: '15%' }}>Tax Id</th>
                                                </tr>
                                            </thead>
                                        </table>
                                        <p>Start filling out the user's name to query the system</p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="dialog-form-row">
                            <RHFInputBox
                                style={{ width: '75%' }}
                                name="ssn"
                                label="Social Security Number"
                                register={register}
                                onChange={() => handleSearch('ssn')}
                                errors={errors}
                                onPaste={(e) => {
                                    e.preventDefault();
                                }}
                                labelOnLeft={true}
                                isRequired={watchStatus === 'E'}
                                spaceLabelContent={true}
                                labelStyle={{ width: '30%' }}
                            />
                            <RHFInputBox
                                style={{ width: '75%' }}
                                name="verifySSN"
                                label="Verify"
                                register={register}
                                errors={errors}
                                isRequired={watchStatus === 'E'}
                                onPaste={(e) => {
                                    e.preventDefault();
                                }}
                                labelOnLeft={true}
                            />
                        </div>
                        <div className="dialog-form-row">
                            <RHFInputBox
                                style={{ width: '75%' }}
                                name="federalTaxId"
                                label="Federal Tax Id"
                                register={register}
                                onChange={() => handleSearch('federalTaxId')}
                                errors={errors}
                                isRequired={watchStatus === 'C' || watchStatus === 'S'}
                                onPaste={(e) => {
                                    e.preventDefault();
                                }}
                                labelOnLeft={true}
                                spaceLabelContent={true}
                                labelStyle={{ width: '30%' }}
                            />
                            <RHFInputBox
                                style={{ width: '75%' }}
                                name="verifyTaxId"
                                label="Verify"
                                register={register}
                                errors={errors}
                                isRequired={watchStatus === 'C' || watchStatus === 'S'}
                                onPaste={(e) => {
                                    e.preventDefault();
                                }}
                                labelOnLeft={true}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '40%', alignItems: 'flex-end' }}>
                            <RHFInputBox
                                style={{ width: 'auto' }}
                                name="defaultPayRate"
                                label="Default Pay Rate"
                                register={register}
                                errors={errors}
                                isRequired={true}
                                onPaste={(e) => {
                                    e.preventDefault();
                                }}
                                className="mr-1"
                            />
                            <RHFSelect
                                name={`defaultPayUnit`}
                                placeholder="Default Pay Unit"
                                options={[
                                    { value: 'D', label: 'Day' },
                                    { value: 'H', label: 'Hour' },
                                    { value: 'M', label: 'Month' },
                                ]}
                                style={{ width: 'auto' }}
                                bodyStyle={{ width: '27%' }}
                                register={register}
                                errors={errors}
                                isRequired={true}
                                className="mr-1"
                                defaultVal={'Hour'}
                            />
                        </div>
                        <div className="dialog-form-row">
                            <RHFSelect
                                style={{ width: '25%' }}
                                name="irsCategoryType"
                                label="IRS Category Type"
                                options={irsCategoryTypeDropdown()}
                                register={register}
                                errors={errors}
                                isRequired={true}
                            />
                        </div>
                    </div>

                </div >
                <div className="button-row">
                    <Button
                        className="blue-hover-button"
                        style={{ width: '24%', marginRight: '1rem' }}
                        text={'Add Personnel'}
                        type="submit"
                        intent="primary"
                    />
                    <Button
                        className="blue-hover-button"
                        style={{ width: '24%', marginRight: '1rem' }}
                        text={'Cancel'}
                        onClick={() => closeDialog()}
                        intent="secondary"
                    />
                </div>
            </form>
        </Dialog >
        <ExistingLmMatchDialog
            dialogName={DialogList.EXISTING_LM_MATCH}
            matches={existingMatches}
            getRoleName={getRoleName}
            onUseExisting={useExistingLmMatch}
            onCreateNew={createNewDespiteMatch}
            onCancel={cancelExistingLmMatch}
        />
        </>
    );
};

export default AddPersonnelDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/AddPersonnelDialog.tsx